import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { WEBSERVICE, SITENAMEALIAS } from '../utils/init';
import { showToast } from '../utils/library';
import './Authorize.css';

export default function Authorize({ isSandbox = 0 }) {
    const location = useLocation();
    const history = useHistory();
    const searchParams = new URLSearchParams(location.search);

    const [isLoading, setIsLoading] = useState(false);
    const [isAppEligible, setIsAppEligible] = useState(false);
    const [appDetails, setAppDetails] = useState(null);
    const [scopesDetails, setScopesDetails] = useState([]);

    // States for URL parameters
    const [clientId, setClientId] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const [scopes, setScopes] = useState('');
    const [state, setState] = useState('');

    // Theme states to match Login page
    const [navbarColor, setNavbarColor] = useState(localStorage.getItem(SITENAMEALIAS + "_navbarColour") || "");
    const [exchangeLogo, setExchangeLogo] = useState(localStorage.getItem(SITENAMEALIAS + "_exchangeLogo") || "");
    const [backgroundImageStatus, setBackgroundImageStatus] = useState(localStorage.getItem(SITENAMEALIAS + "_isBackgroundImage") || 0);
    const [backgroundColour, setBackgroundColour] = useState(localStorage.getItem(SITENAMEALIAS + "_backgroundColour") || "");
    const [backgroundImageLink, setBackgroundImageLink] = useState(localStorage.getItem(SITENAMEALIAS + "_backgroundImageLink") || "");

    /**
     * Function definition to check app eligibility
     */
    const checkAppEligibility = async (clientId, redirectUrl, scopes, uuid) => {
        const payload = {
            merchantId: localStorage.getItem(SITENAMEALIAS + '_merchant_id'),
            clientId,
            redirectUrl,
            scopes: scopes.split(',').map(scope => scope.trim()),
            isSandbox: isSandbox == 1 ? 1 : 0,
        };
        try {
            setIsLoading(true);
            const res = await axios.post(`https://service.hashcashconsultants.com/billbitcoins-v2/app/checkAppEligibility`, JSON.stringify(payload), {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem(SITENAMEALIAS + '_access_token')}`,
                    'UUID': localStorage.getItem(SITENAMEALIAS + '_uuid')
                }
            });
            const error = res.data.error;
            setIsLoading(false);
            if (error.error_data == 1) {
                showToast('error', error.error_msg);
                return false;
            }
            setAppDetails(res.data.appDetails);
            setScopesDetails(res.data.appDetails.scopes || []);
            return true;

        } catch (error) {
            setIsLoading(false);
            showToast('error', 'Failed to verify application eligibility');
            return false;
        }
    };

    /**
     * Function definition to authorize the app
     */
    const handleAuthorizedApp = async () => {
        const payload = {
            merchantId: localStorage.getItem(SITENAMEALIAS + '_merchant_id'),
            clientId: clientId,
            redirectUrl: redirectUrl,
            scopes: scopes.split(',').map(scope => scope.trim()),
            state: state,
            isSandbox: isSandbox == 1 ? 1 : 0
        };

        try {
            setIsLoading(true);
            const res = await axios.post(`https://service.hashcashconsultants.com/billbitcoins-v2/app/authorizeApp`, JSON.stringify(payload), {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem(SITENAMEALIAS + '_access_token')}`,
                    'UUID': localStorage.getItem(SITENAMEALIAS + '_uuid')
                }
            });
            const error = res.data.error;
            setIsLoading(false);

            if (error.error_data == 1) {
                showToast('error', error.error_msg);
                return;
            }

            // Success - redirect to the URL with code
            const responseRedirectUrl = res.data.redirectUrl;
            if (responseRedirectUrl) {
                window.location.href = responseRedirectUrl;
            }

        } catch (error) {
            setIsLoading(false);
            showToast('error', 'Failed to authorize application');
        }
    };

    // Extract URL parameters on component mount
    useEffect(() => {
        const initializeAuthorization = async () => {
            const clientIdParam = searchParams.get('clientId') || '';
            const redirectUrlParam = searchParams.get('redirectUrl') || '';
            const scopesParam = searchParams.get('scopes') || '';
            const statesParam = searchParams.get('state') || '';

            setClientId(clientIdParam);
            setRedirectUrl(redirectUrlParam);
            setScopes(scopesParam);
            setState(statesParam);

            // Validate required parameters
            if (!clientIdParam) {
                showToast('error', 'Client ID is required');
                return;
            }

            if (!scopesParam) {
                showToast('error', 'Scopes are required');
                return;
            }

            // Check if token exists
            const token = localStorage.getItem(SITENAMEALIAS + '_access_token');
            const uuid = localStorage.getItem(SITENAMEALIAS + '_uuid');

            if (!token && !uuid) {
                // Store parameters in localStorage before redirecting
                localStorage.setItem('authorize_clientId', clientIdParam);
                localStorage.setItem('authorize_redirectUrl', redirectUrlParam);
                localStorage.setItem('authorize_scopes', scopesParam);
                localStorage.setItem('authorize_state', statesParam);

                // Redirect to signin page
                history.push('/login');
                return;
            }

            localStorage.removeItem('authorize_clientId');
            localStorage.removeItem('authorize_redirectUrl');
            localStorage.removeItem('authorize_scopes');
            localStorage.removeItem('authorize_state');

            // Check app eligibility if all params are present
            if (clientIdParam && scopesParam) {
                const isEligible = await checkAppEligibility(clientIdParam, redirectUrlParam, scopesParam, uuid);
                setIsAppEligible(isEligible);
            }
        };

        initializeAuthorization();
    }, []);

    return (
        <div className="auth-page-wrapper">
            <nav
                className="navbar navbar-light extNavbar"
                style={{
                    backgroundColor: navbarColor,
                }}
            >
                <div className="image-container">
                    <img src={exchangeLogo} className="logo-cred " alt="Exchange Logo" />
                </div>
            </nav>

            {backgroundImageStatus == 0 ? (
                <div className="login-wrapper" style={{ background: backgroundColour }}>
                    <div className="auth-main-content">
                        <div className="auth-card">
                            {isSandbox == 1 && (
                                <div className="sandbox-banner" style={{ marginTop: '1.5rem' }}>
                                    <i className="fa fa-exclamation-triangle" style={{ color: '#d97706' }}></i>
                                    <p className="sandbox-text">You are in sandbox mode</p>
                                </div>
                            )}

                            <div className="auth-content-layout">
                                <div className="auth-left-side">
                                    <div className="auth-logo-container" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                                        <img src={exchangeLogo} className="auth-logo" style={{ maxWidth: '120px', height: 'auto', objectFit: 'contain' }} alt="Exchange Logo" />
                                    </div>
                                    <div className="auth-header" style={{ textAlign: 'center' }}>
                                        <h1 className="auth-title">Authorize</h1>
                                        <p className="auth-subtitle">to continue to Payments</p>
                                    </div>
                                </div>

                                <div className="auth-right-side">
                                    <div className="auth-form-container">
                                        {isLoading ? (
                                            <div className="loading-spinner-container">
                                                <div>
                                                    <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#2563eb' }}></i>
                                                    <p className="loading-text">Verifying application...</p>
                                                </div>
                                            </div>
                                        ) : isAppEligible && scopes ? (
                                            <>
                                                <div className="app-request-header">
                                                    <div className="app-icon-container">
                                                        {appDetails?.icon ? (
                                                            <img src={appDetails.icon} alt="App icon" style={{ width: '1.75rem', height: '1.75rem', objectFit: 'contain' }} />
                                                        ) : (
                                                            <i className="fa fa-shield-alt fa-2x" style={{ color: '#2563eb' }}></i>
                                                        )}
                                                    </div>
                                                    <div className="app-request-text">
                                                        <p style={{ color: '#4b5563' }}>{appDetails ? appDetails.name : 'App'} wants to access your</p>
                                                        <p style={{ fontWeight: 500, color: '#111827' }}>PayBito Account</p>
                                                    </div>
                                                </div>

                                                <div className="consent-message">
                                                    <div style={{ color: '#000' }}>This will allow the application to:</div>
                                                </div>

                                                <div className="scopes-list">
                                                    {scopesDetails.map((scope, index) => (
                                                        <div key={index} className="scope-item">
                                                            <i className="fa fa-check-circle" style={{ color: '#2563eb', marginTop: '0.25rem' }}></i>
                                                            <p className="scope-text">
                                                                {scope.scopeDescription
                                                                    .trim()
                                                                    .replace(/\bbroker\b/gi, 'your')
                                                                    .charAt(0).toUpperCase() +
                                                                    scope.scopeDescription
                                                                        .trim()
                                                                        .replace(/\bbroker\b/gi, 'your')
                                                                        .slice(1)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="trust-warning">
                                                    <div className="warning-content">
                                                        <i className="fa fa-exclamation-triangle" style={{ color: '#d97706', fontSize: '1.25rem' }}></i>
                                                        <p className="warning-text">
                                                            Make sure you trust this application. By authorizing, you give this app access to your account.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="action-buttons">
                                                    <button
                                                        type="button"
                                                        className="btn-cancel"
                                                        onClick={() => history.goBack()}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleAuthorizedApp}
                                                        className="btn-allow"
                                                    >
                                                        Allow
                                                    </button>
                                                </div>

                                                <div className="revoke-note">
                                                    <p className="revoke-text">You can revoke access at any time from your account settings</p>
                                                </div>
                                            </>
                                        ) : (
                                            !isLoading && (
                                                <div className="auth-error-container">
                                                    <div>
                                                        <div className="error-icon-circle">
                                                            <i className="fa fa-exclamation-triangle fa-2x" style={{ color: '#dc2626' }}></i>
                                                        </div>
                                                        <h3 className="error-title">Authorization Failed</h3>
                                                        <p className="error-message">
                                                            Authorization failed for this application. Please verify the permissions and try again.
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className="login-wrapper"
                    style={{
                        backgroundImage: `url(${backgroundImageLink})`,
                        backgroundRepeat: `no-repeat`,
                        backgroundSize: `cover`,
                        backgroundPosition: `center`,
                        minHeight: `100%`,
                    }}
                >
                    <div className="auth-main-content">
                        <div className="auth-card">
                            {isSandbox == 1 && (
                                <div className="sandbox-banner" style={{ marginTop: '1.5rem' }}>
                                    <i className="fa fa-exclamation-triangle" style={{ color: '#d97706' }}></i>
                                    <p className="sandbox-text">You are in sandbox mode</p>
                                </div>
                            )}

                            <div className="auth-content-layout">
                                <div className="auth-left-side">
                                    <div className="auth-logo-container" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                                        <img src={exchangeLogo} className="auth-logo" style={{ maxWidth: '120px', height: 'auto', objectFit: 'contain' }} alt="Exchange Logo" />
                                    </div>
                                    <div className="auth-header" style={{ textAlign: 'center' }}>
                                        <h1 className="auth-title">Authorize</h1>
                                        <p className="auth-subtitle">to continue to Payments</p>
                                    </div>
                                </div>

                                <div className="auth-right-side">
                                    <div className="auth-form-container">
                                        {isLoading ? (
                                            <div className="loading-spinner-container">
                                                <div>
                                                    <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#2563eb' }}></i>
                                                    <p className="loading-text">Verifying application...</p>
                                                </div>
                                            </div>
                                        ) : isAppEligible && scopes ? (
                                            <>
                                                <div className="app-request-header">
                                                    <div className="app-icon-container">
                                                        {appDetails?.icon ? (
                                                            <img src={appDetails.icon} alt="App icon" style={{ width: '1.75rem', height: '1.75rem', objectFit: 'contain' }} />
                                                        ) : (
                                                            <i className="fa fa-shield-alt fa-2x" style={{ color: '#2563eb' }}></i>
                                                        )}
                                                    </div>
                                                    <div className="app-request-text">
                                                        <p style={{ color: '#4b5563' }}>{appDetails ? appDetails.name : 'App'} wants to access your</p>
                                                        <p style={{ fontWeight: 500, color: '#111827' }}>PayBito Account</p>
                                                    </div>
                                                </div>

                                                <div className="consent-message">
                                                    <p>This will allow <strong>the application</strong> to:</p>
                                                </div>

                                                <div className="scopes-list">
                                                    {scopesDetails.map((scope, index) => (
                                                        <div key={index} className="scope-item">
                                                            <i className="fa fa-check-circle" style={{ color: '#2563eb', marginTop: '0.25rem' }}></i>
                                                            <p className="scope-text">
                                                                {scope.scopeDescription
                                                                    .trim()
                                                                    .replace(/\bbroker\b/gi, 'your')
                                                                    .charAt(0).toUpperCase() +
                                                                    scope.scopeDescription
                                                                        .trim()
                                                                        .replace(/\bbroker\b/gi, 'your')
                                                                        .slice(1)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="trust-warning">
                                                    <div className="warning-content">
                                                        <i className="fa fa-exclamation-triangle" style={{ color: '#d97706', fontSize: '1.25rem' }}></i>
                                                        <p className="warning-text">
                                                            Make sure you trust this application. By authorizing, you give this app access to your account.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="action-buttons">
                                                    <button
                                                        type="button"
                                                        className="btn-cancel"
                                                        onClick={() => history.goBack()}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleAuthorizedApp}
                                                        className="btn-allow"
                                                    >
                                                        Allow
                                                    </button>
                                                </div>

                                                <div className="revoke-note">
                                                    <p className="revoke-text">You can revoke access at any time from your account settings</p>
                                                </div>
                                            </>
                                        ) : (
                                            !isLoading && (
                                                <div className="auth-error-container">
                                                    <div>
                                                        <div className="error-icon-circle">
                                                            <i className="fa fa-exclamation-triangle fa-2x" style={{ color: '#dc2626' }}></i>
                                                        </div>
                                                        <h3 className="error-title">Authorization Failed</h3>
                                                        <p className="error-message">
                                                            Authorization failed for this application. Please verify the permissions and try again.
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
